<template>
	<div class="export-status-widget" v-if="visible">
		<div class="widget-header">
			<div class="widget-title">
				<v-icon :color="statusColor" size="20">{{ statusIcon }}</v-icon>
				<span>{{ t('text.exportStatus', 'Export Status') }}</span>
			</div>
			<v-btn icon size="small" variant="text" @click="dismiss" v-if="canDismiss">
				<v-icon size="16">mdi-close</v-icon>
			</v-btn>
		</div>

		<div class="widget-content">
			<!-- Progress Bar -->
			<div class="progress-section" v-if="isRunning">
				<v-progress-linear
					:model-value="progress"
					:color="statusColor"
					height="8"
					rounded
				></v-progress-linear>
				<div class="progress-text">{{ progress }}%</div>
			</div>

			<!-- Sub-state indicator -->
			<div class="sub-state" v-if="subState">
				<v-chip :color="getSubStateColor(subState)" size="small" variant="tonal">
					{{ getSubStateLabel(subState) }}
				</v-chip>
			</div>

			<!-- Status Message -->
			<div class="status-message" v-if="statusMessage">
				{{ statusMessage }}
			</div>

			<!-- Export Results -->
			<div class="export-results" v-if="!isRunning && (cmResult || peakResult)">
				<!-- Content Hub Result -->
				<div class="system-result" v-if="cmResult">
					<div class="system-header">
						<v-icon :color="getSystemStatusColor(cmResult, 'CM')" size="18">
							{{ getSystemStatusIcon(cmResult, 'CM') }}
						</v-icon>
						<span class="system-name">Content Hub</span>
					</div>
					<div class="system-details">
						<!-- MYS Export Status -->
						<div class="status-row">
							<span class="status-label">MYS Export:</span>
							<span :class="['status-value', cmResult.success ? 'success' : 'error']">
								{{ cmResult.success ? t('text.sent', 'Sent') : t('text.failed', 'Failed') }}
							</span>
							<span v-if="cmResult.mysCompletedAt" class="status-time">
								{{ formatTime(cmResult.mysCompletedAt) }}
							</span>
						</div>
						<!-- External Verification Status -->
						<div class="status-row" v-if="cmResult.externalVerification">
							<span class="status-label">{{ t('text.externalStatus', 'External Status') }}:</span>
							<span :class="['status-value', cmResult.externalVerification.verified ? 'success' : 'error']">
								{{ cmResult.externalVerification.verified ? t('text.published', 'Published') : t('text.notPublished', 'Not Published') }}
							</span>
							<span v-if="cmResult.externalVerification.externalTimestamp" class="status-time">
								{{ formatTime(cmResult.externalVerification.externalTimestamp) }}
							</span>
						</div>
						<div class="status-row verification-note" v-if="cmResult.externalVerification && !cmResult.externalVerification.verified">
							<v-icon size="14" color="warning">mdi-alert</v-icon>
							<span class="note-text">{{ cmResult.externalVerification.error || t('text.verificationFailed', 'Verification failed') }}</span>
						</div>
						<div class="status-row stale-warning" v-if="hasStaleTimestamp(cmResult, 'CM')">
							<v-icon size="14" color="warning">mdi-alert</v-icon>
							<span class="note-text warning-text">{{ t('text.staleTimestampWarning', 'External timestamp is older than MYS export - data may not be synced') }}</span>
						</div>
					</div>
				</div>

				<!-- PEAK Result -->
				<div class="system-result" v-if="peakResult">
					<div class="system-header">
						<v-icon :color="getSystemStatusColor(peakResult)" size="18">
							{{ getSystemStatusIcon(peakResult) }}
						</v-icon>
						<span class="system-name">PEAK</span>
					</div>
					<div class="system-details">
						<!-- MYS Export Status -->
						<div class="status-row">
							<span class="status-label">MYS Export:</span>
							<span :class="['status-value', peakResult.success ? 'success' : 'error']">
								{{ peakResult.success ? t('text.sent', 'Sent') : t('text.failed', 'Failed') }}
							</span>
							<span v-if="peakResult.mysCompletedAt" class="status-time">
								{{ formatTime(peakResult.mysCompletedAt) }}
							</span>
						</div>
						<!-- External Verification Status -->
						<div class="status-row" v-if="peakResult.externalVerification">
							<span class="status-label">{{ t('text.externalStatus', 'External Status') }}:</span>
							<span :class="['status-value', peakResult.externalVerification.verified ? 'success' : 'error']">
								{{ peakResult.externalVerification.verified ? t('text.found', 'Found') : t('text.notFound', 'Not Found') }}
							</span>
							<span v-if="peakResult.externalVerification.externalTimestamp" class="status-time">
								{{ formatTime(peakResult.externalVerification.externalTimestamp) }}
							</span>
						</div>
						<div class="status-row verification-note" v-if="peakResult.externalVerification && !peakResult.externalVerification.verified">
							<v-icon size="14" color="warning">mdi-alert</v-icon>
							<span class="note-text">{{ peakResult.externalVerification.error || t('text.verificationFailed', 'Verification failed') }}</span>
						</div>
						<div class="status-row stale-warning" v-if="hasStaleTimestamp(peakResult)">
							<v-icon size="14" color="warning">mdi-alert</v-icon>
							<span class="note-text warning-text">{{ t('text.staleTimestampWarning', 'External timestamp is older than MYS export - data may not be synced') }}</span>
						</div>
						<div class="status-row" v-if="!peakResult.externalVerification && peakResult.success">
							<span class="note-text muted">{{ t('text.verificationNotAvailable', 'External verification not available') }}</span>
						</div>
					</div>
				</div>
			</div>

			<!-- Error Section with Retry -->
			<div class="error-section" v-if="hasError">
				<v-alert type="error" variant="tonal" density="compact" class="mb-2">
					{{ errorMessage }}
				</v-alert>
				<v-btn
					v-if="canRetry"
					color="warning"
					variant="outlined"
					size="small"
					@click="handleRetry"
					:loading="retrying"
				>
					<v-icon start size="16">mdi-restart</v-icon>
					{{ retryButtonLabel }}
				</v-btn>
			</div>

			<!-- Success indicator -->
			<div class="success-section" v-if="isComplete && !hasError">
				<v-icon color="success" size="24">mdi-check-circle</v-icon>
				<span>{{ t('text.exportComplete', 'Export Complete') }}</span>
			</div>
		</div>
	</div>
</template>

<script>
import Common from '@/mixins/Common.vue'
import BackgroundJobExecutive from '../../../../api2/src/modules/jobs/BackgroundJobExecutive'

export default {
	name: 'ExportStatusWidget',
	mixins: [Common],
	props: {
		jobId: String,
		productId: String,
		clientId: String,
		autoStart: { type: Boolean, default: true },
	},
	emits: ['complete', 'retry', 'dismissed'],

	data() {
		return {
			job: null,
			loading: false,
			retrying: false,
			dismissed: false,
			pollInterval: null,
			currentJobId: null,
			backgroundJobExecutive: new BackgroundJobExecutive(this),
		}
	},

	computed: {
		visible() {
			return !this.dismissed && (this.currentJobId || this.job)
		},
		isRunning() {
			return this.job?.status === 'NEW' || this.job?.status === 'RUNNING'
		},
		isComplete() {
			return this.job?.status === 'DONE'
		},
		hasError() {
			return this.job?.status === 'ERROR'
		},
		canDismiss() {
			return !this.isRunning
		},
		canRetry() {
			return this.hasError && this.failedTarget
		},
		failedTarget() {
			const subState = this.job?.statusData?.subState
			if (subState === 'CMFailed') return 'CM'
			if (subState === 'PEAKFailed') return 'PEAK'
			return null
		},
		progress() {
			return this.job?.statusData?.progress || 0
		},
		subState() {
			return this.job?.statusData?.subState
		},
		statusMessage() {
			const message = this.job?.statusData?.message
			if (!message) return ''
			return message[this.selectedLocale] || message.de || message.en || ''
		},
		errorMessage() {
			return this.job?.statusData?.error || this.t('text.exportFailed', 'Export failed')
		},
		cmResult() {
			return this.job?.statusData?.cmExportResult
		},
		peakResult() {
			return this.job?.statusData?.peakExportResult
		},
		statusColor() {
			if (this.isComplete && !this.hasError) return 'success'
			if (this.hasError) return 'error'
			if (this.isRunning) return 'primary'
			return 'grey'
		},
		statusIcon() {
			if (this.isComplete && !this.hasError) return 'mdi-check-circle'
			if (this.hasError) return 'mdi-alert-circle'
			if (this.isRunning) return 'mdi-sync'
			return 'mdi-circle-outline'
		},
		retryButtonLabel() {
			return this.failedTarget === 'CM'
				? this.t('text.retryContentHub', 'Retry Content Hub')
				: this.t('text.retryPeak', 'Retry PEAK')
		}
	},

	watch: {
		jobId: {
			immediate: true,
			handler(newJobId) {
				if (newJobId) {
					this.currentJobId = newJobId
					this.dismissed = false
					if (this.autoStart) {
						this.startPolling()
					}
				}
			}
		}
	},

	methods: {
		// Helper method to translate with fallback for missing keys
		t(key, fallback) {
			const translated = this.$t(key)
			// If the translation returns the key itself, use the fallback
			return translated === key || translated.startsWith('text.') ? fallback : translated
		},

		async fetchJobStatus() {
			if (!this.currentJobId) return

			try {
				const response = await this.backgroundJobExecutive.getJobStatus(this.currentJobId)
				this.job = response.job

				// Stop polling if job is complete or errored
				if (!response.isActive) {
					this.stopPolling()
					this.$emit('complete', this.job)
				}
			} catch (error) {
				console.error('Failed to fetch job status:', error)
			}
		},

		startPolling() {
			this.fetchJobStatus()
			this.pollInterval = setInterval(() => {
				this.fetchJobStatus()
			}, 2000) // Poll every 2 seconds
		},

		stopPolling() {
			if (this.pollInterval) {
				clearInterval(this.pollInterval)
				this.pollInterval = null
			}
		},

		async handleRetry() {
			if (!this.failedTarget || !this.currentJobId) return

			this.retrying = true
			try {
				// Emit retry event for parent to handle, including the job ID
				this.$emit('retry', this.failedTarget, this.currentJobId)
			} catch (error) {
				console.error('Failed to retry job:', error)
				this.showError(this.t('text.failedToRetryExport', 'Failed to retry export'))
			} finally {
				this.retrying = false
			}
		},

		dismiss() {
			this.dismissed = true
			this.stopPolling()
			this.$emit('dismissed')
		},

		getSubStateColor(subState) {
			const colors = {
				'SendingToCM': 'blue',
				'PollingCM': 'blue',
				'CMSynced': 'green',
				'CMFailed': 'red',
				'SendingToPEAK': 'orange',
				'PollingPEAK': 'orange',
				'PEAKSynced': 'green',
				'PEAKFailed': 'red'
			}
			return colors[subState] || 'grey'
		},

		getSubStateLabel(subState) {
			const labels = {
				'SendingToCM': this.t('text.sendingToContentHub', 'Sending to Content Hub'),
				'PollingCM': this.t('text.verifyingContentHub', 'Verifying Content Hub'),
				'CMSynced': this.t('text.contentHubSynced', 'Content Hub Synced'),
				'CMFailed': this.t('text.contentHubFailed', 'Content Hub Failed'),
				'SendingToPEAK': this.t('text.sendingToPeak', 'Sending to PEAK'),
				'PollingPEAK': this.t('text.verifyingPeak', 'Verifying PEAK'),
				'PEAKSynced': this.t('text.peakSynced', 'PEAK Synced'),
				'PEAKFailed': this.t('text.peakFailed', 'PEAK Failed')
			}
			return labels[subState] || subState
		},

		formatTime(dateString) {
			if (!dateString) return ''
			return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
		},

		getSystemStatusColor(result, system) {
			if (!result) return 'grey'
			// If MYS export failed, it's red
			if (!result.success) return 'red'
			// If MYS succeeded but verification failed, it's orange (warning)
			if (result.externalVerification && !result.externalVerification.verified) return 'orange'
			// If timestamp is stale, it's orange (warning)
			if (this.hasStaleTimestamp(result, system)) return 'orange'
			// If everything succeeded, it's green
			if (result.externalVerification?.verified) return 'green'
			// If MYS succeeded but no verification available, it's light green
			return 'light-green'
		},

		getSystemStatusIcon(result, system) {
			if (!result) return 'mdi-circle-outline'
			// If MYS export failed, show error
			if (!result.success) return 'mdi-alert-circle'
			// If MYS succeeded but verification failed, show warning
			if (result.externalVerification && !result.externalVerification.verified) return 'mdi-alert'
			// If timestamp is stale, show warning
			if (this.hasStaleTimestamp(result, system)) return 'mdi-alert'
			// If everything succeeded, show check
			if (result.externalVerification?.verified) return 'mdi-check-circle'
			// If MYS succeeded but no verification available, show check with question
			return 'mdi-check-circle-outline'
		},

		hasStaleTimestamp(result, system) {
			if (system === 'CM' && this.job?.configData?.suppressCmStaleTimestampWarning === true) return false
			return result?.externalVerification?.staleTimestamp === true
		}
	},

	beforeUnmount() {
		this.stopPolling()
	}
}
</script>

<style scoped>
.export-status-widget {
	background: white;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	padding: 16px;
	margin-bottom: 16px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.widget-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
}

.widget-title {
	display: flex;
	align-items: center;
	gap: 8px;
	font-weight: 500;
	font-size: 14px;
}

.widget-content {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.progress-section {
	margin-bottom: 4px;
}

.progress-text {
	text-align: right;
	font-size: 12px;
	color: #666;
	margin-top: 4px;
}

.sub-state {
	display: flex;
	align-items: center;
}

.status-message {
	font-size: 13px;
	color: #333;
}

.export-results {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.system-result {
	background: #f8f9fa;
	border-radius: 6px;
	padding: 10px 12px;
}

.system-header {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
}

.system-name {
	font-weight: 600;
	font-size: 13px;
}

.system-details {
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-left: 26px;
}

.status-row {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 12px;
}

.status-label {
	color: #666;
	min-width: 90px;
}

.status-value {
	font-weight: 500;
}

.status-value.success {
	color: #4caf50;
}

.status-value.error {
	color: #f44336;
}

.status-time {
	color: #888;
	font-size: 11px;
	margin-left: auto;
}

.verification-note {
	margin-top: 4px;
}

.note-text {
	font-size: 11px;
	color: #666;
}

.note-text.muted {
	font-style: italic;
	color: #999;
}

.note-text.warning-text {
	color: #e65100;
}

.stale-warning {
	background: #fff8e1;
	padding: 6px 8px;
	border-radius: 4px;
	border-left: 3px solid #ff9800;
	margin-top: 6px;
}

.error-section {
	margin-top: 4px;
}

.success-section {
	display: flex;
	align-items: center;
	gap: 8px;
	color: #4caf50;
	font-weight: 500;
	font-size: 14px;
}
</style>
